import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  Alert,
  Empty,
  ICONS,
  Icon,
  KIT_CSS,
  relative,
  useStyleSheet,
} from '../invoices/kit';
import { FEEDBACK_CSS } from './feedbackCss';
import { FilterBar, ScreenRollup, StateTabs } from './BoardToolbar';
import { ImpactTag, StatePill, TypeTag } from './Pills';
import TriagePanel from './TriagePanel';
import {
  EMPTY_FILTERS,
  boardCsv,
  boardCsvName,
  countsByState,
  filterItems,
  filtersFromSearch,
  groupByScreen,
  searchFromFilters,
  sortItems,
} from './lib/boardFilters';
import { SCREEN_OPTIONS, screenLabel } from './lib/screens';
import { getFeedbackBoard } from '../../api_handlers/feedback';

/**
 * The tech team's board.
 *
 * It lives on its own top-level route rather than inside the planner
 * shell, because triage wants the full width and the people doing it are
 * not using the planner at the time.
 *
 * Filters are held in the URL so a triage call can be a pasted link. The
 * list and the panel share one fetched array: saving an item patches that
 * array in place rather than re-fetching, so the board does not jump
 * under someone mid-session.
 */

const SCREEN_IDS = SCREEN_OPTIONS.map((screen) => screen.id);

function download(name, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function ItemRow({ item, selected, onPick }) {
  const by = item.submitted_by || {};
  return (
    <li>
      <button
        type="button"
        className="fb-item"
        aria-current={selected}
        onClick={() => onPick(item)}
      >
        <span className="fb-item__top">
          <StatePill state={item.state} />
          <TypeTag type={item.type} />
          <ImpactTag impact={item.impact} />
        </span>
        <p className="fb-item__title">{item.title}</p>
        <p className="fb-item__detail">{item.detail}</p>
        <span className="fb-item__foot">
          <span>{screenLabel(item.screen)}</span>
          <span aria-hidden="true">·</span>
          <span>{by.name || by.email || 'Unknown'}</span>
          <span aria-hidden="true">·</span>
          <span>{relative(item.created_at)}</span>
          {item.owner_name || item.owner ? (
            <>
              <span aria-hidden="true">·</span>
              <span>{item.owner_name || item.owner}</span>
            </>
          ) : null}
        </span>
      </button>
    </li>
  );
}

export default function FeedbackBoard() {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('fb-kit', FEEDBACK_CSS);

  const history = useHistory();
  const location = useLocation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState('');
  const [selectedId, setSelectedId] = useState('');

  const filters = useMemo(
    () => filtersFromSearch(location.search, SCREEN_IDS),
    [location.search],
  );

  const load = useCallback(() => {
    setLoading(true);
    setFailure('');
    return getFeedbackBoard()
      .then(setItems)
      .catch((error) => setFailure(error.message || 'We could not load the board.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (field, value) => {
    const next = { ...filters, [field]: value };
    history.replace({ pathname: location.pathname, search: searchFromFilters(next) });
  };

  const counts = useMemo(() => countsByState(items), [items]);
  const visible = useMemo(
    () => sortItems(filterItems(items, filters), filters.sort),
    [items, filters],
  );
  const rollup = useMemo(() => groupByScreen(items), [items]);
  const selected = useMemo(
    () => visible.find((item) => String(item.id) === String(selectedId)) || null,
    [visible, selectedId],
  );

  // Replace the saved row in place. Re-fetching would reorder the list
  // under whoever is working through it.
  const onSaved = (saved) => {
    setItems((current) =>
      current.map((item) => (String(item.id) === String(saved.id) ? { ...item, ...saved } : item)),
    );
  };

  const filtered = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  return (
    <div className="iv-root iv-page">
      <div className="iv-wrap iv-wrap--console">
        <div className="iv-head">
          <div>
            <h1 className="iv-h1">Feedback board</h1>
            <p className="iv-lede">
              Everything raised from the dashboard, by brand users and by the team. Move an item
              along and the person who raised it sees the status and your reply.
            </p>
          </div>
          <button type="button" className="iv-btn iv-btn--ghost" onClick={load} disabled={loading}>
            {loading ? <span className="iv-spin" /> : <Icon path={ICONS.clock} size={15} />}
            Refresh
          </button>
        </div>

        {failure ? <Alert onRetry={load}>{failure}</Alert> : null}

        <StateTabs
          value={filters.status}
          counts={counts}
          onChange={(status) => setFilter('status', status)}
        />

        <FilterBar
          filters={filters}
          onChange={setFilter}
          onExport={() => download(boardCsvName(), boardCsv(visible))}
          exportDisabled={visible.length === 0}
        />

        <div className="fb-board">
          <div>
            {loading ? <p className="fb-note">Loading the board…</p> : null}

            {!loading && visible.length === 0 ? (
              <Empty icon={ICONS.sheet} title={filtered ? 'Nothing matches' : 'Nothing raised yet'}>
                {filtered
                  ? 'Widen the filters, or clear them to see the whole board.'
                  : 'When someone uses the Feedback button in the planner header, it lands here.'}
              </Empty>
            ) : null}

            {visible.length > 0 ? (
              <ul className="fb-list">
                {visible.map((item) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    selected={String(item.id) === String(selectedId)}
                    onPick={(picked) => setSelectedId(String(picked.id))}
                  />
                ))}
              </ul>
            ) : null}
          </div>

          <aside className="fb-board__side">
            <TriagePanel item={selected} onSaved={onSaved} />
            <ScreenRollup
              rows={rollup}
              active={filters.screen}
              onPick={(id) => setFilter('screen', id)}
            />
          </aside>
        </div>
      </div>
    </div>
  );
}
